import { EmailTypeEnum } from '../../../../types/working-model';
import { EmailContentProps } from './session-email-content';
import { EmailTextPhrases, namePlaceholder } from './event-emails';
import useEditableEmailFields from './use-editable-email-fields';
import AddToCalendar from './add-to-calendar';
import { OptionalComponent } from '../../../../utils/optional-component';
import { TextAlignmentsEnum } from '../../../../types/template-layouts';


const EventEmailContent = ({
	emailType,
	design,
	emailContent,
	customContent,
	onEdit,
	sent,
	forceRtl,
	hideCalendar
}: EmailContentProps): JSX.Element => {
	if (!emailContent.event) return (<></>);

	const textPhrase = (key: keyof Omit<EmailTextPhrases, 'subject'>) => {
		return emailContent.translations[key];
	};
	const isRegistrationConfirmationEmail = emailType === EmailTypeEnum.event_registration_email;

	// registration uses personalized greeting, event email does not
	const salutationText = isRegistrationConfirmationEmail ?
		`${textPhrase('salutation2')} ${namePlaceholder}, ${textPhrase('registered')}`
		: `${textPhrase('salutation2')}, ${textPhrase('invited')}`;

	const buttonText = textPhrase((!isRegistrationConfirmationEmail && emailContent.event.registrationRequired) ? 'RegisterNow' : 'ExploreEvent');

	interface IEditableContent {
		salutation: JSX.Element;
		eventName: JSX.Element;
		eventDescription: JSX.Element;
		eventStart: JSX.Element;
	}

	const editableContent: IEditableContent = {
		salutation: (<h4 style={design.styles.h4}><span style={design.styles.h4Span}>{salutationText}</span></h4>),
		eventName: (<h3 style={design.styles.h3}><span style={design.styles.h3Span}>{emailContent.event.name}</span></h3>),
		eventDescription: (<p style={design.styles.p}><span style={design.styles.pSpan}>{emailContent.event.description}</span></p>),
		eventStart: (<h6 style={design.styles.h6}><span style={design.styles.h6Span}>{textPhrase('eventStart')}</span></h6>),
	};

	const {
		getCellContent,
		getButton,
		getImage,
		EditImageModal
	} = useEditableEmailFields<IEditableContent>({ onEdit, sent, design, emailContent, emailContentType: 'event', editableContent, customContent, buttonText });

	const alignment = (ltrLanguageAlign = TextAlignmentsEnum.center) => forceRtl ? TextAlignmentsEnum.right : ltrLanguageAlign;
	const direction = forceRtl ? 'rtl' : 'ltr';

	return (<>
		<tr>
			{getImage()}
		</tr>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('salutation')}
			</td>
		</tr>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('eventName')}
			</td>
		</tr>
		<tr>
			<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
				{getCellContent('eventDescription')}
			</td>
		</tr>
		<OptionalComponent display={!!emailContent.event.start && emailContent.settings?.showStartTime}><>
			<tr className='bl-event-showtime' >
				<td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
					{getCellContent('eventStart')}
				</td>
			</tr>
			<tr className='bl-event-showtime' >
				<td align={alignment()} dir={direction}>
					<p style={{ ...design.styles.accent, ...design.styles.accentSpan }}>{emailContent.event.start}</p>
				</td>
			</tr>
		</></OptionalComponent>
		<tr style={{ "height": "55px" }}>
			<td align={alignment()} dir={direction}>
				{getButton()}
			</td>
		</tr>
		<OptionalComponent display={emailContent.settings?.showAddCalendar && !hideCalendar}>
			<AddToCalendar
				emailContent={emailContent}
				design={design}
				emailType={emailType}
				customContent={customContent}
				onEdit={onEdit}
				sent={sent}
			/>
		</OptionalComponent>
		{EditImageModal}
	</>);
};

export default EventEmailContent;